import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CaseloadMap } from '../components/CaseloadMap.tsx';
import { LIST_BATCH, useDirectorDashboard, useDirectorList, type DirectorFilters } from '../api/dashboards.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import {
  Chip, DashboardFrame, Waiting, listState, useUrlFilters,
} from '../components/DashboardFrame.tsx';
import { ListEnd } from '../components/ListEnd.tsx';
import { ScrollToTop } from '../components/ScrollToTop.tsx';
import { BulkAssignBar } from '../components/BulkAssignBar.tsx';
import { setRowLoader } from '../components/revealRegistry.ts';
import { useKeepPlace } from '../components/useKeepPlace.ts';
import { formatDate } from '../dates.ts';
import type {
  ArrearsBand, CaseManagerCard, CountySection, DirectorColumns, DirectorRow, DirectorView,
  MapPlace, PlaceCard, PlaceClient,
} from '../api/types.ts';

/**
 * Bands in the order a director reads them: furthest behind first. Every mark
 * says its band in words. Colour is reserved for Critical flags, so none of
 * these borrows it — being behind on visits is not a safety warning.
 *
 * "On pace", not "Done": under the owed-period denominator a client at pace in
 * March is keeping up, not finished.
 */
const BANDS: { band: ArrearsBand; key: 'high' | 'medium' | 'low' | 'done' | 'none'; label: string; mark: string }[] = [
  { band: 'High', key: 'high', label: 'High', mark: 'bg-warning-bg text-warning-fg ring-1 ring-warning-edge' },
  { band: 'Medium', key: 'medium', label: 'Medium', mark: 'bg-note-bg text-note-fg ring-1 ring-note-edge' },
  { band: 'Low', key: 'low', label: 'Low', mark: 'bg-white text-neutral-700 ring-1 ring-neutral-300' },
  { band: 'Done', key: 'done', label: 'On pace', mark: 'bg-white text-neutral-500 ring-1 ring-neutral-200' },
  { band: 'None', key: 'none', label: 'None owed', mark: 'bg-white text-neutral-500 ring-1 ring-neutral-200' },
];

/**
 * What the client list can be sorted by. Whichever is chosen, clients it ranks
 * equal are ordered the same way: never seen first, then lowest pace. That is
 * the server's tiebreak, not an option here.
 */
const SORTS: { value: string; label: string }[] = [
  { value: 'priority', label: 'Priority, lowest pace first' },
  { value: 'location', label: 'Location, by city then state' },
  { value: 'community', label: 'Community clients first' },
  { value: 'name', label: 'Client name' },
  { value: 'appointment_date', label: 'Appointment date' },
];
const DEFAULT_SORT = 'priority';

/**
 * The columns shown when the server has not said which — both of them.
 *
 * A module constant rather than a literal in the render, because it is passed to
 * every row: built inline it was a new object each time, which defeats the memo
 * on `ClientRow` for the entire list and leaves every append reconciling every
 * row already on screen.
 */
const BOTH_COLUMNS: DirectorColumns = { social_work: true, finance: true };

/**
 * Pace as a whole percent, TRUNCATED, never rounded (migration 033).
 *
 * The band is decided on the exact pace. A rounded percentage reached
 * thresholds the band had not — "50%" beside High, "100%" beside Low — on
 * about 1,600 client-days of 2026. The stored ratio is truncated to three
 * places, so multiplying by 1000 recovers a whole number exactly; dividing
 * by ten and flooring truncates again, and can only move the number down.
 */
function pacePercent(ratio: number): number {
  return Math.floor(Math.round(ratio * 1000) / 10);
}

/** The ends of the board's colour scale: no pace at all, and full pace or better. */
const LOW_PACE = [255, 105, 180] as const; // hot pink
const FULL_PACE = [173, 216, 230] as const; // light blue

/**
 * A place's colour, from its clients' average pace, on a FIXED scale: 0 is hot
 * pink, full pace and above light blue, and everything between in proportion.
 * Never relative to the other places on screen, so a colour means the same pace
 * under every filter and on every day. A place where nobody owes a visit has no
 * pace, and is grey.
 */
function paceColour(averagePace: number | null): string {
  if (averagePace === null) return 'rgb(229, 229, 229)';
  const t = Math.min(Math.max(averagePace, 0), 1);
  const [r, g, b] = LOW_PACE.map((low, i) => Math.round(low + (FULL_PACE[i]! - low) * t));
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * The visits dashboard.
 *
 * Two ways in. **By client** lists everyone on the caseloads the caller can
 * see, sorted as chosen. **By location** is a board of cards, one per place a
 * client is now, grouped by county and coloured by the average pace there;
 * a card opens the clients at that place.
 *
 * The one ordering rule holds in both, under every sort and at every level:
 * never-seen clients above everyone else in their cohort, and lower pace above
 * higher.
 *
 * Case managers open it over their own caseload, and read the other
 * department's case manager rather than a column naming themselves on every
 * row. Anyone seeing across caseloads reads both, and gets a card per case
 * manager at the top.
 */
export function DirectorDashboard() {
  return (
    <DashboardFrame
      dashboard="director"
      title="Visits"
      subtitle="This year’s visits for each client, against the visits owed so far for the period each client has been owed them. Pace below half is High, below three-quarters Medium, below full pace Low."
    >
      <DirectorBody />
    </DashboardFrame>
  );
}

const managerValue = (m: CaseManagerCard) => m.id ?? 'no-social-work';
const managerName = (m: CaseManagerCard) => m.name ?? 'No SW case manager';

/** The bands a card counts. Not "None owed": the cards count visit work, and it would always read 0. */
const CARD_BANDS = BANDS.filter(
  (b): b is (typeof BANDS)[number] & { key: 'high' | 'medium' | 'low' | 'done' } => b.band !== 'None',
);

function DirectorBody() {
  const [params, update] = useUrlFilters();
  const viewParam = params.get('view');
  const view: DirectorView =
    viewParam === 'locations' ? 'locations' : viewParam === 'map' ? 'map' : 'clients';
  const band = params.get('band') ?? undefined;
  const neverSeen = params.get('never_seen') === 'true';
  const exempt = params.get('exempt') === 'true';
  const caseManager = params.get('case_manager') ?? undefined;
  const sort = view === 'clients' ? (params.get('sort') ?? DEFAULT_SORT) : undefined;

  // Every filter and the sort are in the key, so changing one is a different
  // list: the loaded rows are discarded and it starts at the top, in the new
  // order, from the server. Nothing reorders the rows already loaded.
  const filters: DirectorFilters = {
    view,
    case_manager: caseManager,
    band,
    never_seen: neverSeen ? 'true' : undefined,
    exempt: exempt ? 'true' : undefined,
    sort,
  };
  const filterKey = JSON.stringify(filters);
  const list = useDirectorList(filters, view === 'clients');
  const query = useDirectorDashboard({ ...filters, offset: 0, limit: 1 }, view !== 'clients');

  // The board is one request; the client list grows as it is scrolled. Both
  // report what the rows on screen may claim in the same shape.
  const { failure, takeover } = listState(view === 'clients'
    ? { error: list.error, failureReason: list.failureReason, isPlaceholderData: list.isPlaceholderData, data: list.head }
    : query);
  const data = takeover ? undefined : (view === 'clients' ? list.head : query.data);
  const counts = data?.band_counts ?? {};
  const all = Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0);
  const clients = takeover ? [] : list.rows;
  const counties = data?.locations ?? [];
  const places = data?.places ?? [];
  const unmappable = data?.unmappable ?? [];
  const empty =
    view === 'clients' ? clients.length === 0
    : view === 'map'   ? places.length === 0 && unmappable.length === 0
    : counties.length === 0;
  // The fallback is a module constant, not a literal built here. As a literal it
  // was a new object on every render, passed to every ClientRow — which would
  // defeat the memo on that row silently, leaving the whole list reconciling on
  // every append and the memo looking like it had simply not helped.
  const columns: DirectorColumns = data?.context.columns ?? BOTH_COLUMNS;
  const managers = data?.case_managers ?? [];

  // `lost` is rendered below: a restore that could not get back to where the
  // reader was must say so. Landing silently at the top is what made this look
  // like the list had simply forgotten them.
  const place = useKeepPlace(`visits:${filterKey}`, list, LIST_BATCH);

  /**
   * Bulk assignment, offered on the "No SW case manager" card and nowhere else.
   * That card is the one place on the board where "give these to somebody" is
   * the obvious next act; on any other filter a checkbox column would be noise
   * on every row a director reads all day.
   *
   * `togglePick` is stable, so ticking one row does not re-render the rest —
   * `ClientRow` is memoised and only the row whose `picked` changed comes back.
   */
  const selecting =
    view === 'clients' && caseManager === 'no-social-work' && data?.context.may_assign === true;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [assigned, setAssigned] = useState<number | null>(null);
  const togglePick = useCallback((clientId: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (!next.delete(clientId)) next.add(clientId);
      return next;
    });
  }, []);
  // Leaving the card drops the selection rather than carrying it to a filter
  // where it cannot be seen or undone.
  useEffect(() => { if (!selecting) { setPicked(new Set()); setAssigned(null); } }, [selecting]);

  // The quick search asks here before it navigates: a client further down this
  // list is fetched and jumped to, rather than the reader being taken to their
  // record and losing their place.
  useEffect(() => {
    if (view !== 'clients') return;
    setRowLoader(async (clientId) => {
      const { found } = await list.loadUntil((rows) => rows.some((r) => r.client_id === clientId));
      return found;
    });
    return () => setRowLoader(null);
  }, [view, list]);

  return (
    <>
      {data?.context.across_caseloads && managers.length > 0 && (
        <ManagerCards
          managers={managers}
          caseManager={caseManager}
          band={band}
          onPick={(value, pickedBand) =>
            update({ case_manager: value, band: pickedBand, never_seen: null, offset: null })}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 px-4 py-3 lg:px-5">
        <div className="flex flex-wrap gap-2" role="group" aria-label="View">
          <Chip active={view === 'clients'} onClick={() => update({ view: null, offset: null })}>
            By client
          </Chip>
          <Chip active={view === 'locations'} onClick={() => update({ view: 'locations', offset: null })}>
            By location
          </Chip>
          {/* A THIRD view, not a replacement. The board answers "which places
              are behind, worst first"; the map answers "where are they". */}
          <Chip active={view === 'map'} onClick={() => update({ view: 'map', offset: null })}>
            On a map
          </Chip>
        </div>
        <label htmlFor="director-case-manager" className="sr-only">Case manager</label>
        <select
          id="director-case-manager"
          value={caseManager ?? ''}
          onChange={(e) => update({ case_manager: e.target.value, offset: null })}
          className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2 text-[0.9375rem]
                     text-neutral-800"
        >
          <option value="">Every case manager</option>
          <optgroup label="SW case managers">
            {managers.map((m) => (
              <option key={managerValue(m)} value={managerValue(m)}>
                {managerName(m)} · {m.high + m.medium} behind of {m.total}
                {m.never_seen ? ` · ${m.never_seen} never seen` : ''}
              </option>
            ))}
          </optgroup>
          {(data?.finance_case_managers.length ?? 0) > 0 && (
            <optgroup label="Finance case managers">
              {data!.finance_case_managers.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
              <option value="no-finance">No finance case manager</option>
            </optgroup>
          )}
        </select>
        {view === 'clients' && sort && (
          <>
            <label htmlFor="director-sort" className="sr-only">Sort by</label>
            <select
              id="director-sort"
              value={sort}
              onChange={(e) => update({ sort: e.target.value === DEFAULT_SORT ? null : e.target.value, offset: null })}
              className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2 text-[0.9375rem]
                         text-neutral-800"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>Sort: {s.label}</option>)}
              {/* a link saved before these sorts still opens, and says so */}
              {!SORTS.some((s) => s.value === sort) && <option value={sort}>Sort: as in the link</option>}
            </select>
          </>
        )}
        <Chip active={exempt} onClick={() => update({ exempt: exempt ? null : 'true', offset: null })}>
          Show visit-exempt clients <span className="font-normal">{data?.exempt ?? 0}</span>
        </Chip>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-neutral-200 px-4 py-3 lg:px-5" role="group" aria-label="Priority">
        <Chip active={!band && !neverSeen} onClick={() => update({ band: null, never_seen: null, offset: null })}>
          All <span className="font-normal">{all}</span>
        </Chip>
        <Chip active={neverSeen} onClick={() => update({ never_seen: 'true', band: null, offset: null })}>
          Never seen <span className="font-normal">{data?.never_seen ?? 0}</span>
        </Chip>
        {BANDS.map((b) => (
          <Chip
            key={b.band}
            active={band === b.band}
            onClick={() => update({ band: b.band, never_seen: null, offset: null })}
          >
            {b.label} <span className="font-normal">{counts[b.band] ?? 0}</span>
          </Chip>
        ))}
      </div>

      {selecting && (
        <>
          <BulkAssignBar
            picked={[...picked]}
            total={list.total ?? clients.length}
            onClear={() => setPicked(new Set())}
            onDone={(n) => { setPicked(new Set()); setAssigned(n); }}
          />
          {assigned !== null && (
            <p role="status" className="border-b border-neutral-200 bg-neutral-50 px-5 py-2 text-sm text-neutral-700">
              {assigned} {assigned === 1 ? 'client' : 'clients'} assigned. They have left this card.
            </p>
          )}
        </>
      )}

      {!takeover && (
        <Staleness
          updatedAt={view === 'clients' ? list.dataUpdatedAt : query.dataUpdatedAt}
          isFetching={view === 'clients' ? list.loading && !list.appending : query.isFetching}
          error={view === 'clients' ? list.error : query.error}
          onRefresh={() => (view === 'clients' ? list.refetch() : void query.refetch())}
        />
      )}

      {failure && takeover ? (
        <LoadFailure
          what="Visits"
          error={failure}
          onRetry={() => (view === 'clients' ? list.refetch() : void query.refetch())}
          retrying={view === 'clients' ? list.loading : query.isFetching}
          forbiddenIsSession={false}
        />
      ) : (view === 'clients' ? list.pending : query.isPending) ? (
        <Waiting>Counting this year’s visits…</Waiting>
      ) : !takeover && empty ? (
        <Waiting>No clients match these filters.</Waiting>
      ) : view === 'clients' ? (
        <>
          {/* Getting back to where the reader was is the only thing that loads
              rows without them scrolling, and it says so while it does it. A
              restore that could not get there says that too: landing silently
              at the top is what made this read as the list having forgotten
              them. */}
          {place.restoring && (
            <p role="status" className="border-b border-neutral-200 bg-neutral-50 px-5 py-2 text-sm text-neutral-600">
              Getting you back to where you were…
            </p>
          )}
          {place.lost && (
            <p role="status" className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-sm text-amber-900">
              {place.lost}
            </p>
          )}
          {/* sticky: the columns stay readable however far down the list runs,
              which they did not need to be while it was paged */}
          <div className={`sticky top-0 z-10 hidden border-b border-neutral-200 bg-white px-5 py-3 text-xs
                           font-bold tracking-[0.1em] text-neutral-500 uppercase ${gridFor(columns)} xl:!grid`}>
            <span>Client</span>
            <span>Appointment Type</span>
            {columns.social_work && <span>SW Case Manager</span>}
            {columns.finance && <span>Finance Case Manager</span>}
            <span>Residence Type</span>
            <span>Location</span>
            <span>Last Visit</span>
            <span className="text-right">Priority</span>
          </div>
          <ol className="divide-y divide-neutral-200">
            {clients.map((row) => (
              <ClientRow
                key={row.client_id}
                row={row}
                columns={columns}
                picked={selecting ? picked.has(row.client_id) : null}
                onPick={selecting ? togglePick : null}
              />
            ))}
          </ol>
          {/* asks for the next batch a screenful early, and holds its own height
              so appending never moves the rows above it */}
          <ListEnd
            loadMore={list.loadMore}
            hasMore={list.hasMore}
            appending={list.appending}
            loaded={clients.length}
            total={list.total}
            noun="clients"
          />
        </>
      ) : view === 'map' ? (
        <MapPanel
          places={places}
          unmappable={unmappable}
          unmappableClients={data?.unmappable_clients ?? 0}
        />
      ) : (
        <FacilityBoard counties={counties} />
      )}

      <ScrollToTop />
    </>
  );
}

/**
 * A card per social work case manager, for anyone seeing across caseloads:
 * their clients in each band, "No SW case manager" included. The name narrows
 * the list to them; a band narrows it to them and that band.
 *
 * Social work only. Visits are social work's responsibility, and finance cards
 * counted every client a second time. Who handles a client's money is the
 * Finance column.
 */
function ManagerCards({
  managers, caseManager, band, onPick,
}: {
  managers: CaseManagerCard[];
  caseManager: string | undefined;
  band: string | undefined;
  onPick: (caseManager: string, band: string | null) => void;
}) {
  return (
    <section aria-label="By case manager" className="border-b border-neutral-200 px-4 py-3 lg:px-5">
      <h2 className="text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">SW case managers</h2>
      <ul className="mt-2 mb-1 grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {managers.map((m) => {
          const value = managerValue(m);
          const chosen = caseManager === value;
          return (
            <li key={value} className={`rounded-lg border bg-white p-2 ${chosen ? 'border-neutral-900' : 'border-neutral-200'}`}>
              <button
                type="button"
                onClick={() => onPick(value, null)}
                aria-pressed={chosen && !band}
                className="block min-h-[44px] w-full rounded text-left hover:bg-neutral-50"
              >
                <span className="block font-semibold text-neutral-900">{managerName(m)}</span>
                <span className="block text-sm text-neutral-600 tabular-nums">
                  {m.total} {m.total === 1 ? 'client' : 'clients'}
                  {m.never_seen > 0 && ` · ${m.never_seen} never seen`}
                </span>
              </button>
              <div className="mt-1 flex flex-wrap gap-1">
                {CARD_BANDS.map((b) => (
                  <button
                    key={b.band}
                    type="button"
                    onClick={() => onPick(value, b.band)}
                    aria-pressed={chosen && band === b.band}
                    aria-label={`${managerName(m)}: ${b.label}, ${m[b.key]}`}
                    className={`min-h-[44px] min-w-[44px] rounded px-2 text-xs font-bold tracking-wide uppercase
                                tabular-nums ${b.mark} ${chosen && band === b.band ? 'outline-2 outline-neutral-900' : ''}`}
                  >
                    {b.label} {m[b.key]}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The column grid, for whichever case manager columns this caller reads. */
function gridFor(columns: DirectorColumns): string {
  if (columns.social_work && columns.finance) {
    return 'xl:grid xl:grid-cols-[minmax(0,1.2fr)_9.5rem_minmax(0,9rem)_minmax(0,9rem)_8rem_minmax(0,1.5fr)_10rem_7rem] xl:items-start xl:gap-3';
  }
  if (columns.social_work || columns.finance) {
    return 'xl:grid xl:grid-cols-[minmax(0,1.2fr)_9.5rem_minmax(0,10rem)_8rem_minmax(0,1.7fr)_10rem_7rem] xl:items-start xl:gap-3';
  }
  return 'xl:grid xl:grid-cols-[minmax(0,1.3fr)_9.5rem_8rem_minmax(0,1.9fr)_10rem_7rem] xl:items-start xl:gap-3';
}

function BandMark({ band }: { band: ArrearsBand }) {
  const style = BANDS.find((b) => b.band === band)!;
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-bold tracking-wide uppercase ${style.mark}`}>
      {style.label}
    </span>
  );
}

/** The band, and beneath it the pace it was read from: two readings of one number. */
function Priority({ row }: { row: DirectorRow }) {
  return (
    <div className="text-right">
      <BandMark band={row.arrears_band} />
      {row.pace_ratio !== null && (
        <p className="mt-0.5 text-xs text-neutral-600 tabular-nums">{pacePercent(row.pace_ratio)}% of pace</p>
      )}
    </div>
  );
}

/**
 * Appointed and never visited. Not a band: a client under guardianship for
 * months who has never been seen is a different problem from one who has
 * fallen behind, so it is its own mark, in words, in every place the client
 * appears.
 */
function NeverSeenMark() {
  return (
    <span className="inline-flex rounded bg-neutral-900 px-2 py-0.5 text-xs font-bold tracking-wide text-white uppercase">
      Never seen
    </span>
  );
}

function LastVisit({ row }: { row: DirectorRow }) {
  if (row.never_seen) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <NeverSeenMark />
        {row.days_without_visit !== null && (
          <span className="text-xs text-neutral-700 tabular-nums">{row.days_without_visit} days since appointment</span>
        )}
      </div>
    );
  }
  if (!row.last_visit_date) return <span className="text-sm text-neutral-500">No visit on record</span>;
  return (
    <p className="text-sm text-neutral-700 tabular-nums xl:text-[0.9375rem]">
      {formatDate(row.last_visit_date)}
      {row.days_without_visit !== null && (
        <span className="block text-xs text-neutral-500">{row.days_without_visit} days ago</span>
      )}
    </p>
  );
}

/** The location's name, and beneath it, smaller, its address. */
function Location({ row }: { row: DirectorRow }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-sm text-neutral-800 xl:text-[0.9375rem]">{row.location ?? 'Not recorded'}</p>
      {row.location_address && <p className="truncate text-xs text-neutral-500">{row.location_address}</p>}
      {row.on_temporary_stay && (
        <p className="truncate text-xs text-neutral-500">Temporary stay · lives at {row.lives_at ?? 'not recorded'}</p>
      )}
    </div>
  );
}

/**
 * Memoised, for the reason set out on the Reports row: an append re-renders the
 * page, and without this every row already on screen re-rendered with it, so
 * adding fifty rows cost the reconciliation of all of them.
 *
 * Both props must be stable for it to bite. `row` is, because react-query keeps
 * each page object across an append. `columns` is, because its fallback is
 * `BOTH_COLUMNS` above rather than an object literal built in the render.
 */
const ClientRow = memo(function ClientRow({
  row, columns, picked, onPick,
}: {
  row: DirectorRow;
  columns: DirectorColumns;
  /** Null when nothing is being selected — the ordinary board. */
  picked: boolean | null;
  onPick: ((clientId: string) => void) | null;
}) {
  return (
    // what the quick search can jump to on this page (QuickSearch.tsx)
    <li
      data-quick-client={row.client_id}
      data-quick-location={row.location_key ?? 'not-recorded'}
      className={picked === null ? undefined : 'flex items-center'}
    >
      {picked !== null && onPick && (
        // Outside the Link, not inside it: a checkbox within an anchor is
        // activated by the anchor's own click and the row navigates away.
        <label className="flex min-h-[64px] shrink-0 cursor-pointer items-center pl-4 lg:pl-5">
          <input
            type="checkbox"
            checked={picked}
            onChange={() => onPick(row.client_id)}
            aria-label={`Select ${row.display_name}`}
            className="size-5 rounded border-neutral-400"
          />
        </label>
      )}
      <Link
        to={`/clients/${row.client_id}`}
        // so the client's back control returns here, by name, rather than to the
        // client list — which would leave this list, and the reader's place in it
        state={{ from: 'Visits' }}
        className={`block min-h-[64px] space-y-0.5 px-4 py-3 hover:bg-neutral-50 active:bg-neutral-100
                    xl:space-y-0 xl:px-5 xl:py-2.5 ${picked === null ? '' : 'min-w-0 flex-1'}
                    ${gridFor(columns)}`}
      >
        <p className="min-w-0 truncate font-semibold text-neutral-900">{row.display_name}</p>
        <p className="text-sm text-neutral-700 xl:text-[0.9375rem]">
          <span className="text-neutral-500 xl:hidden">Appointment: </span>{row.appointment_type ?? 'No order on file'}
        </p>
        {columns.social_work && (
          <p className="truncate text-sm text-neutral-600 xl:text-[0.9375rem]">
            <span className="xl:hidden">SW: </span>{row.case_manager ?? 'No SW case manager'}
          </p>
        )}
        {columns.finance && (
          <p className="truncate text-sm text-neutral-600 xl:text-[0.9375rem]">
            <span className="xl:hidden">Finance: </span>{row.finance_case_manager ?? 'No finance case manager'}
          </p>
        )}
        <p className="text-sm text-neutral-600 xl:text-[0.9375rem]">
          <span className="xl:hidden">Residence type: </span>{row.residence_type ?? 'Not recorded'}
        </p>
        <Location row={row} />
        <div className="flex items-start justify-between gap-3 pt-1 xl:contents">
          <LastVisit row={row} />
          <Priority row={row} />
        </div>
      </Link>
    </li>
  );
});

const cardId = (place: PlaceCard) => `place-${place.location_key.replace(/[^A-Za-z0-9_-]/g, '-')}`;

/**
 * The facility board: a card per place a client is now, in sections by county.
 *
 * A card shows the place's name and nothing else. Its colour is the average of
 * its clients' pace on the fixed scale of `paceColour`, and a red dot marks a
 * place where anyone has never been seen. Neither is colour alone: the card's
 * accessible name says both in words, and the dialog lists every client's band.
 *
 * No case manager anywhere on the board. A client on a temporary stay is on the
 * card for where they are, not where they live.
 */
function FacilityBoard({ counties }: { counties: CountySection[] }) {
  const [open, setOpen] = useState<PlaceCard | null>(null);
  // Focus goes back to the card only once the dialog has gone: while a modal
  // dialog is open the rest of the page is inert and refuses focus.
  const returnTo = useRef<string | null>(null);
  useEffect(() => {
    if (open || !returnTo.current) return;
    document.getElementById(returnTo.current)?.focus();
    returnTo.current = null;
  }, [open]);

  return (
    <div className="px-4 py-3 lg:px-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-neutral-600">
        <p>
          Every client in view, at the place they are now. A card’s colour is the average pace of the
          clients there.
        </p>
        <div className="flex items-center gap-2" aria-hidden="true">
          <span className="text-xs tabular-nums">0%</span>
          <span
            className="h-3 w-40 rounded-full ring-1 ring-neutral-300"
            style={{ background: `linear-gradient(to right, ${paceColour(0)}, ${paceColour(1)})` }}
          />
          <span className="text-xs tabular-nums">100% of pace or more</span>
        </div>
        <p className="flex items-center gap-1.5">
          <span className="inline-block size-2.5 rounded-full bg-red-600" aria-hidden="true" />
          Someone here has never been seen
        </p>
      </div>

      {counties.map((section) => (
        <section key={section.county ?? 'no-county'} aria-labelledby={`county-${section.county ?? 'none'}`} className="mt-4">
          <h2 id={`county-${section.county ?? 'none'}`} className="text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
            {section.county ?? 'County not recorded'}
          </h2>
          <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
            {section.locations.map((place) => (
              <li key={place.location_key}>
                <button
                  id={cardId(place)}
                  type="button"
                  onClick={() => { returnTo.current = cardId(place); setOpen(place); }}
                  aria-haspopup="dialog"
                  aria-label={placeSummary(place)}
                  data-quick-location={place.location_key}
                  data-quick-client={place.clients.map((c) => c.client_id).join(' ')}
                  className="relative block min-h-[72px] w-full rounded-lg px-3 py-2 text-left text-sm font-semibold
                             text-neutral-900 ring-1 ring-black/10 hover:ring-2 hover:ring-neutral-900"
                  style={{ backgroundColor: paceColour(place.average_pace) }}
                >
                  {place.never_seen > 0 && (
                    <span className="absolute top-2 right-2 size-2.5 rounded-full bg-red-600 ring-2 ring-white" aria-hidden="true" />
                  )}
                  <span className="block pr-4 break-words">{place.location ?? 'Location not recorded'}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {open && <PlaceDialog place={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function placeSummary(place: PlaceCard): string {
  const pace = place.average_pace === null ? 'no visits owed' : `average ${pacePercent(place.average_pace)}% of pace`;
  const clients = `${place.client_count} ${place.client_count === 1 ? 'client' : 'clients'}`;
  const never = place.never_seen > 0 ? `, ${place.never_seen} never seen` : '';
  return `${place.location ?? 'Location not recorded'}: ${pace}, ${clients}${never}`;
}

/**
 * The clients at one place, in a modal dialog: name, band, and days since the
 * last visit. Native `<dialog>`, so Escape closes it and focus stays inside;
 * closing returns focus to the card that opened it.
 */
function PlaceDialog({ place, onClose }: { place: PlaceCard; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const closed = useRef(onClose);
  closed.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // A fallback only: the close event is queued, not dispatched, and a
    // background tab may never deliver it. Left to that event alone, the
    // dialog closed but stayed mounted, and its card would not open it again.
    const handle = () => closed.current();
    dialog.addEventListener('close', handle);
    if (!dialog.open) dialog.showModal();
    return () => dialog.removeEventListener('close', handle);
  }, []);

  /** Every way out: Escape, Close, and the backdrop. Closing twice is harmless. */
  const dismiss = () => {
    if (ref.current?.open) ref.current.close();
    closed.current();
  };

  return (
    <dialog
      ref={ref}
      aria-labelledby="place-dialog-title"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); dismiss(); } }}
      // a click on the backdrop lands on the dialog element itself
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      className="m-auto w-[min(40rem,calc(100vw-2rem))] rounded-xl p-0 shadow-xl backdrop:bg-black/40"
    >
      <div className="flex items-start gap-3 border-b border-neutral-200 px-4 py-3">
        <div className="min-w-0">
          <h2 id="place-dialog-title" className="text-lg font-semibold text-neutral-900">
            {place.location ?? 'Location not recorded'}
          </h2>
          {place.location_address && <p className="text-xs text-neutral-500">{place.location_address}</p>}
          <p className="text-sm text-neutral-600 tabular-nums">
            {place.client_count} {place.client_count === 1 ? 'client' : 'clients'}
            {place.never_seen > 0 && ` · ${place.never_seen} never seen`}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="ml-auto min-h-[44px] min-w-[44px] rounded-lg px-3 text-sm font-semibold text-neutral-700
                     ring-1 ring-neutral-300 hover:bg-neutral-50"
        >
          Close
        </button>
      </div>
      <ol className="max-h-[70vh] divide-y divide-neutral-100 overflow-y-auto">
        {place.clients.map((client) => (
          <li key={client.client_id}>
            <Link
              to={`/clients/${client.client_id}`}
              state={{ from: 'Visits' }}
              className="flex min-h-[56px] flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 hover:bg-neutral-50
                         active:bg-neutral-100"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-neutral-900">{client.display_name}</span>
                {client.on_temporary_stay && (
                  <span className="block truncate text-xs text-neutral-500">
                    Temporary stay · lives at {client.lives_at ?? 'not recorded'}
                  </span>
                )}
              </span>
              <DaysSince client={client} />
              <span className="w-24 text-right"><BandMark band={client.arrears_band} /></span>
            </Link>
          </li>
        ))}
      </ol>
    </dialog>
  );
}

function DaysSince({ client }: { client: PlaceClient }) {
  if (client.never_seen) {
    return (
      <span className="flex flex-wrap items-center gap-x-2">
        <NeverSeenMark />
        {client.days_without_visit !== null && (
          <span className="text-xs text-neutral-700 tabular-nums">{client.days_without_visit} days since appointment</span>
        )}
      </span>
    );
  }
  if (client.days_without_visit === null || !client.last_visit_date) {
    return <span className="text-sm text-neutral-500">No visit on record</span>;
  }
  return (
    <span className="text-sm text-neutral-700 tabular-nums">{client.days_without_visit} days since last visit</span>
  );
}

/**
 * The map, and an honest account of who is not on it.
 *
 * **The panel below the map is not a footnote.** A client whose place has no
 * coordinate cannot be a pin, and the tempting thing — leave them off — makes
 * the map look complete while it is missing people. On the seed that is 85
 * clients with no placement recorded at all, which is a gap in what the agency
 * knows rather than a gap in the geocoding, and it is written up in
 * `docs/handover.md` as work for the real data migration.
 *
 * So they are counted, named, and reachable. The map is never the whole answer
 * on its own, and it says so.
 */
function MapPanel({
  places, unmappable, unmappableClients,
}: {
  places: MapPlace[];
  unmappable: MapPlace[];
  unmappableClients: number;
}) {
  const [open, setOpen] = useState<PlaceCard | null>(null);
  const pinned = places.reduce((n, p) => n + Number(p.client_count), 0);

  return (
    <div className="p-4 lg:p-5">
      <CaseloadMap places={places} onOpenPlace={(place) => setOpen(place)} />

      <p className="mt-3 text-sm text-neutral-600">
        {pinned} client{pinned === 1 ? '' : 's'} at {places.length} place
        {places.length === 1 ? '' : 's'}. Bigger circles hold more clients; colour is
        the same pace scale as the board, and a red ring means someone there has never
        been seen.
      </p>

      {unmappableClients > 0 && (
        <section className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <h3 className="text-sm font-bold tracking-[0.12em] text-amber-900 uppercase">
            Not on the map — {unmappableClients} client{unmappableClients === 1 ? '' : 's'}
          </h3>
          <p className="mt-1 text-amber-900">
            These clients are in this view and cannot be placed. They are counted
            everywhere else on this dashboard; only the map cannot show them.
          </p>
          <ul className="mt-2 space-y-1">
            {unmappable.map((place) => (
              <li key={place.location_key}>
                <button
                  type="button"
                  onClick={() => setOpen(place)}
                  className="min-h-[44px] text-left font-semibold text-amber-900 underline"
                >
                  {place.location ?? 'No placement recorded'} — {place.client_count} client
                  {place.client_count === 1 ? '' : 's'}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {open && <PlaceDialog place={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
